import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  TrendingUp, TrendingDown, ChevronDown, ChevronRight, RefreshCw, Download,
  CheckCircle2, AlertCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

export default function VarianceAuditPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canApprove = !!(user?.is_owner || user?.can_approve_variances);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expanded, setExpanded] = useState({}); // { entry_id: true }
  const [approveDialog, setApproveDialog] = useState({
    open: false, entry: null, reason: '', submitting: false,
  });
  const [metric, setMetric] = useState({ pending_count: 0, total_pending_variance_amount: 0 });

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/variance-entries?limit=500&status=${statusFilter}`;
      const [listRes, metricRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API_URL}/api/variance-entries/owner-dashboard-metric`, { headers }),
      ]);
      setEntries(listRes.data?.entries || []);
      setMetric(metricRes.data || { pending_count: 0, total_pending_variance_amount: 0 });
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied. Variance Audit module access required.');
      } else {
        toast.error('Failed to load variance entries');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); /* eslint-disable-next-line */ }, [statusFilter]);

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const openApprove = (entry) =>
    setApproveDialog({ open: true, entry, reason: '', submitting: false });
  const closeApprove = () =>
    setApproveDialog({ open: false, entry: null, reason: '', submitting: false });

  const submitApprove = async () => {
    if (!approveDialog.entry) return;
    setApproveDialog((p) => ({ ...p, submitting: true }));
    try {
      const url = `${API_URL}/api/variance-entries/${approveDialog.entry.id}/approve`;
      await axios.post(url, { reason: approveDialog.reason || null }, { headers });
      toast.success('Variance entry approved');
      closeApprove();
      fetchEntries();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || 'Approval failed'
      );
      setApproveDialog((p) => ({ ...p, submitting: false }));
    }
  };

  const exportExcel = () => {
    if (!entries.length) {
      toast.info('Nothing to export');
      return;
    }
    const rows = entries.map((e) => ({
      'WO Number': e.work_order_number,
      'Status': e.status,
      'Expected Material Cost': Number(e.expected_material_cost || 0),
      'Actual Material Cost': Number(e.actual_material_cost || 0),
      'Variance Amount': Number(e.variance_amount || 0),
      'Variance %': Number(e.variance_percent || 0),
      'Triggered By': e.triggered_by,
      'Created At': e.created_at,
      'Last Refreshed': e.last_refreshed_at,
      'Approved By': e.approved_by_user_name || '',
      'Approved At': e.approved_at || '',
      'Approval Reason': e.approval_reason || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Variance Audit');
    XLSX.writeFile(
      wb,
      `variance-audit-${statusFilter}-${format(new Date(), 'yyyyMMdd-HHmm')}.xlsx`
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-indigo-600" />
              Variance Audit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Per-WO comparison of BOM-mandated material cost (expected) against
              canonical FIFO actual material cost. Pending entries refresh
              daily; approved entries are frozen.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchEntries} data-testid="va-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="va-export-btn">
              <Download className="h-4 w-4 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="va-metric-pending-count">
            <CardHeader className="pb-2">
              <CardDescription>Pending Variances</CardDescription>
              <CardTitle className="text-3xl">{metric.pending_count}</CardTitle>
            </CardHeader>
          </Card>
          <Card data-testid="va-metric-pending-amount">
            <CardHeader className="pb-2">
              <CardDescription>Total Pending Variance</CardDescription>
              <CardTitle className="text-3xl">
                {formatINR(metric.total_pending_variance_amount)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Status Filter</CardDescription>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="va-status-filter" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
          </Card>
        </div>

        {/* Entry table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {entries.length} {statusFilter === 'all' ? '' : statusFilter} entry/entries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>WO</TableHead>
                    <TableHead className="text-right">Expected</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">%</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Approved</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                        Loading...
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && entries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                        No entries
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && entries.map((e) => {
                    const isOpen = !!expanded[e.id];
                    const positive = (e.variance_amount || 0) > 0;
                    return (
                      <React.Fragment key={e.id}>
                        <TableRow data-testid={`va-row-${e.work_order_number}`}>
                          <TableCell>
                            <button
                              onClick={() => toggleExpand(e.id)}
                              className="text-muted-foreground hover:text-foreground"
                              data-testid={`va-expand-${e.work_order_number}`}
                            >
                              {isOpen
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              className="font-mono text-sm text-indigo-700 hover:underline"
                              onClick={() => navigate(`/work-orders/${e.work_order_id}`)}
                            >
                              {e.work_order_number}
                            </button>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatINR(e.expected_material_cost)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatINR(e.actual_material_cost)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            <span className={`inline-flex items-center gap-1 ${
                              positive ? 'text-red-600' : 'text-emerald-600'
                            }`}>
                              {positive
                                ? <TrendingUp className="h-3 w-3" />
                                : <TrendingDown className="h-3 w-3" />}
                              {formatINR(Math.abs(e.variance_amount || 0))}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {Number(e.variance_percent || 0).toFixed(2)}%
                          </TableCell>
                          <TableCell>
                            {e.status === 'approved' ? (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Approved
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                <AlertCircle className="h-3 w-3 mr-1" /> Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {e.approved_by_user_name && (
                              <div>
                                <div>{e.approved_by_user_name}</div>
                                <div className="text-muted-foreground">
                                  {e.approved_at
                                    ? format(parseISO(e.approved_at), 'dd MMM yyyy HH:mm')
                                    : ''}
                                </div>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {e.status === 'pending' && canApprove && (
                              <Button
                                size="sm"
                                onClick={() => openApprove(e)}
                                data-testid={`va-approve-btn-${e.work_order_number}`}
                              >
                                Approve
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-slate-50 p-0">
                              <div className="px-6 py-4">
                                <div className="text-xs font-semibold text-muted-foreground mb-2">
                                  Material breakdown ({(e.material_breakdown || []).length} line(s))
                                </div>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Item</TableHead>
                                      <TableHead className="text-right">Expected qty</TableHead>
                                      <TableHead className="text-right">Actual qty</TableHead>
                                      <TableHead className="text-right">Expected cost</TableHead>
                                      <TableHead className="text-right">Actual cost</TableHead>
                                      <TableHead className="text-right">Line variance</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {(e.material_breakdown || []).map((m, mi) => (
                                      <TableRow key={`${m.item_id}-${mi}`}>
                                        <TableCell className="text-sm">
                                          {m.item_name || m.item_id}
                                          {m.unit && <span className="text-xs text-muted-foreground ml-1">({m.unit})</span>}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm">
                                          {Number(m.expected_qty || 0).toFixed(3)}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm">
                                          {Number(m.actual_qty || 0).toFixed(3)}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm">
                                          {formatINR(m.expected_cost)}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-sm">
                                          {formatINR(m.actual_cost)}
                                        </TableCell>
                                        <TableCell className={`text-right font-mono text-sm ${
                                          (m.line_variance || 0) > 0 ? 'text-red-600' :
                                          (m.line_variance || 0) < 0 ? 'text-emerald-600' : ''
                                        }`}>
                                          {formatINR(m.line_variance)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                {e.approval_reason && (
                                  <div className="mt-3 text-xs">
                                    <span className="font-semibold">Approval reason:</span>{' '}
                                    <span className="text-muted-foreground">{e.approval_reason}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Approve dialog */}
        <Dialog
          open={approveDialog.open}
          onOpenChange={(o) => { if (!o) closeApprove(); }}
        >
          <DialogContent data-testid="va-approve-dialog">
            <DialogHeader>
              <DialogTitle>
                Approve variance — {approveDialog.entry?.work_order_number}
              </DialogTitle>
              <DialogDescription>
                Variance: {formatINR(approveDialog.entry?.variance_amount)} (
                {Number(approveDialog.entry?.variance_percent || 0).toFixed(2)}%).
                Once approved, daily refresh will skip this entry.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                rows={4}
                placeholder="Damage / rework / wastage / operational deviation..."
                value={approveDialog.reason}
                onChange={(e) =>
                  setApproveDialog((p) => ({ ...p, reason: e.target.value }))
                }
                data-testid="va-approve-reason"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeApprove}>Cancel</Button>
              <Button
                onClick={submitApprove}
                disabled={approveDialog.submitting}
                data-testid="va-approve-confirm-btn"
              >
                {approveDialog.submitting ? 'Approving...' : 'Confirm Approval'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
